import api from './api';
import {
  ApiResponse,
  ValidateCouponRequest,
  ValidateCouponResponse,
  ApplyCouponRequest,
  ApplyCouponResponse,
  Coupon,
} from '../types/api';

type RawCoupon = Omit<Coupon, 'type' | 'validFrom' | 'validUntil'> & {
  type: string;
  validFrom: string | Date;
  validUntil: string | Date;
};

const normalizeCoupon = (coupon?: RawCoupon | null): Coupon | undefined => {
  if (!coupon) return undefined;

  const normalizedType = coupon.type.toLowerCase();
  const allowedTypes: Coupon['type'][] = ['percentage', 'fixed', 'free_shipping'];

  return {
    ...coupon,
    type: allowedTypes.includes(normalizedType as Coupon['type'])
      ? (normalizedType as Coupon['type'])
      : 'percentage',
    validFrom: coupon.validFrom instanceof Date ? coupon.validFrom : new Date(coupon.validFrom),
    validUntil: coupon.validUntil instanceof Date ? coupon.validUntil : new Date(coupon.validUntil),
  };
};

const extractData = <T>(payload: ApiResponse<T> | T): T => {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    const maybeData = (payload as ApiResponse<T>).data;
    if (maybeData) {
      return maybeData;
    }
  }

  if (payload) {
    return payload as T;
  }

  throw new Error('Resposta inválida da API de cupons');
};

/**
 * Serviço de Cupons
 */
const couponService = {
  /**
   * Valida um cupom de desconto
   */
  async validate(code: string, data: ValidateCouponRequest): Promise<ValidateCouponResponse> {
    const response = await api.get<ApiResponse<ValidateCouponResponse> | ValidateCouponResponse>(
      `/coupons/validate/${code}`,
      {
        params: data,
      },
    );

    const result = extractData(response.data);

    return {
      ...result,
      coupon: normalizeCoupon(result.coupon as RawCoupon | undefined),
    };
  },

  /**
   * Aplica cupom ao carrinho
   */
  async apply(data: ApplyCouponRequest): Promise<ApplyCouponResponse> {
    const response = await api.post<ApiResponse<ApplyCouponResponse> | ApplyCouponResponse>(
      '/coupons/apply',
      data,
    );

    const result = extractData(response.data);

    return {
      ...result,
      coupon: normalizeCoupon(result.coupon as RawCoupon | undefined) as Coupon,
    };
  },
};

export default couponService;
